import React, { useCallback, useEffect, useState } from 'react';

import { Text, useMantineTheme, Button } from '@mantine/core';
import { IconCircleOff, IconCircleCheckFilled } from '@tabler/icons-react';
import Table from 'components/Table';
import { useAuth } from 'context/auth-context';
import useListStaff from 'hooks/staff/useListStaff';
import { useParams } from 'react-router-dom';
import { CellProps } from 'react-table';
import { StaffList } from 'types/generated';
import { formatPhoneNumber } from 'utils/formatters';

const AdminStaffTableContainer = ({
  searchFilters,
}: {
  searchFilters: {
    searchTerm: string;
    isArrangeByLastName: boolean;
  };
}): JSX.Element => {
  const theme = useMantineTheme();
  const [ordering, setOrdering] = useState('firstName');
  const [ascending, setAscending] = useState(true);
  const [tableData, setTableData] = useState<StaffList[]>([]);
  const params = useParams<{ [key: string]: string }>();

  const { impersonate } = useAuth();

  const orderingValue =
    ordering === 'firstName' && searchFilters.isArrangeByLastName ? 'lastName' : ordering;

  const { data, fetchNextPage, hasNextPage, isSuccess, isLoading, isError, isFetchingNextPage } =
    useListStaff({
      ...searchFilters,
      ordering: ascending ? orderingValue : `-${orderingValue}`,
      gymId: params.gymId || '',
    });

  const handleHeaderClick = useCallback(
    (header: string): void => {
      setOrdering(header);
      setAscending(!ascending);
    },
    [ascending],
  );

  const columns = [
    {
      Header: 'Name',
      accessor: 'firstName' as const,
      Cell: ({ row }: CellProps<StaffList>) => <Text weight='bold'>{row.original.fullName}</Text>,
    },
    {
      Header: 'Staff Id',
      accessor: 'staffId' as const,
    },
    {
      Header: 'Email',
      accessor: 'email' as const,
    },
    {
      Header: 'Coach',
      accessor: 'isCoach' as const,
      Cell: ({ value }: CellProps<StaffList>) =>
        value ? (
          <IconCircleCheckFilled size={theme.fontSizes.xl} style={{ color: theme.colors.kiloBlue[6] }} />
        ) : (
          <IconCircleOff size={theme.fontSizes.lg} color='gray' />
        ),
    },
    {
      Header: 'Phone',
      accessor: 'phone' as const,
      Cell: ({ value }: CellProps<StaffList>) => formatPhoneNumber(value),
    },
    {
      id: 'actions',
      Cell: ({ row }: CellProps<StaffList>) => (
        <Button compact variant='outline' onClick={() => impersonate(row.original.profile)}>
          Impersonate
        </Button>
      ),
      sticky: true,
    },
  ];

  useEffect(() => {
    if (!isLoading && data) {
      setTableData(data.pages.map(page => page.data.results).flat());
    }
  }, [isLoading, data, setTableData]);

  return (
    <>
      <Table
        tableData={tableData}
        columns={columns}
        isSuccess={isSuccess}
        isLoading={isLoading}
        isError={isError}
        isFetchingNextPage={isFetchingNextPage}
        hasNextPage={!!hasNextPage}
        fetchNextPage={fetchNextPage}
        clickableHeaders={['firstName', 'email', 'isCoach']}
        handleHeaderClick={handleHeaderClick}
        ascending={ascending}
        ordering={ordering}
      />
    </>
  );
};

export default AdminStaffTableContainer;
